export const toAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

export const roundMoney = (value) => Math.round((toAmount(value) + Number.EPSILON) * 100) / 100;

export const getOrderItemRevenue = (item) => Math.max(
  roundMoney(toAmount(item.quantity) * toAmount(item.unitPrice) - toAmount(item.discount)),
  0
);

export const getOrderItemCost = (item) => roundMoney(
  toAmount(item.quantity) * (
    item.materialId
      ? toAmount(item.materialQuantityPerUnit || 1) * toAmount(item.materialUnitCost)
      : toAmount(item.customUnitCost)
  )
);

export const getOrderTotals = (order) => {
  const items = order.items || [];
  const productSubtotal = roundMoney(items.reduce(
    (total, item) => total + getOrderItemRevenue(item),
    0
  ));
  const discountPercentage = Math.min(Math.max(toAmount(order.discountPercentage), 0), 100);
  const orderDiscount = roundMoney(productSubtotal * (discountPercentage / 100));
  const productRevenue = roundMoney(productSubtotal - orderDiscount);
  const materialCost = roundMoney(items.reduce(
    (total, item) => total + getOrderItemCost(item),
    0
  ));
  const shippingCharged = toAmount(order.shippingCharged);
  const shippingCost = toAmount(order.shippingCost);

  return {
    productSubtotal,
    discountPercentage,
    orderDiscount,
    productRevenue,
    materialCost,
    shippingCharged,
    shippingCost,
    invoiceTotal: roundMoney(productRevenue + shippingCharged),
    grossProfit: roundMoney(productRevenue + shippingCharged - materialCost - shippingCost)
  };
};

export const getOrderCollected = (order, collections = []) => roundMoney(
  toAmount(order.deposit) + collections
    .filter((collection) => collection.orderId === order.id)
    .reduce((total, collection) => total + toAmount(collection.amount), 0)
);

export const getOrderOutstanding = (order, collections = []) => Math.max(
  roundMoney(getOrderTotals(order).invoiceTotal - getOrderCollected(order, collections)),
  0
);

export const getMonthlyReport = ({
  month, orders = [], losses = [], expenses = [], collections = []
}) => {
  const monthlyOrders = orders.filter((order) => (order.date || '').startsWith(month));
  const monthlyLosses = losses.filter((loss) => (loss.date || '').startsWith(month));
  const monthlyExpenses = expenses.filter((expense) => (expense.date || '').startsWith(month));
  const monthlyCollections = collections.filter(
    (collection) => (collection.date || '').startsWith(month)
  );
  const orderTotals = monthlyOrders.map(getOrderTotals);
  const productRevenue = roundMoney(orderTotals.reduce(
    (total, order) => total + order.productRevenue,
    0
  ));
  const shippingRevenue = roundMoney(orderTotals.reduce(
    (total, order) => total + order.shippingCharged,
    0
  ));
  const materialCost = roundMoney(orderTotals.reduce(
    (total, order) => total + order.materialCost,
    0
  ));
  const shippingCost = roundMoney(orderTotals.reduce(
    (total, order) => total + order.shippingCost,
    0
  ));
  const lossCost = roundMoney(monthlyLosses.reduce(
    (total, loss) => total + toAmount(loss.calculatedAmount || loss.amount),
    0
  ));
  const expenseCost = roundMoney(monthlyExpenses.reduce(
    (total, expense) => total + toAmount(expense.amount),
    0
  ));
  const deposits = roundMoney(monthlyOrders.reduce(
    (total, order) => total + toAmount(order.deposit),
    0
  ));
  const courierCollected = roundMoney(monthlyCollections.reduce(
    (total, collection) => total + toAmount(collection.amount),
    0
  ));
  const revenue = roundMoney(productRevenue + shippingRevenue);
  const totalCosts = roundMoney(materialCost + shippingCost + lossCost + expenseCost);
  const cashCollected = roundMoney(deposits + courierCollected);

  return {
    orderCount: monthlyOrders.length,
    productRevenue,
    shippingRevenue,
    revenue,
    materialCost,
    shippingCost,
    lossCost,
    expenseCost,
    totalCosts,
    netProfit: roundMoney(revenue - totalCosts),
    deposits,
    courierCollected,
    cashCollected,
    actualRevenue: cashCollected,
    actualNetProfit: roundMoney(cashCollected - totalCosts)
  };
};

export const formatAccountingMoney = (value) => `${roundMoney(value).toLocaleString()} ج.م`;
